/**
 * HeatCyclingPage
 *
 * Operational dashboard showing substrate batches in heat treatment (sterilisation).
 * Displays batches in HEAT_CYCLING or COOLING status with equipment details.
 *
 * Allows operators to mark cooled batches as ready (after 24+ hours cooling).
 */

import { useState } from 'react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useHeatCyclingBatches, useHeatCyclingMutations } from '@/hooks/useHeatCyclingBatches'
import { RefreshCw, AlertTriangle, Loader2, Thermometer, Snowflake } from 'lucide-react'
import SteamBarrelCard from '@/components/production/heat-cycles/SteamBarrelCard'
import CycleHistoryTable from '@/components/production/heat-cycles/CycleHistoryTable'

export default function HeatCyclingPage({ user, onLogout }) {
  const { batches, loading, error, refetch } = useHeatCyclingBatches()
  const { markAsReady, loading: mutationLoading } = useHeatCyclingMutations()
  const [processingIds, setProcessingIds] = useState(new Set())

  const handleMarkReady = async (batch) => {
    setProcessingIds(prev => new Set(prev).add(batch.id))
    try {
      await markAsReady(batch.id)
      refetch()
    } catch (err) {
      console.error('Failed to mark as ready:', err)
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(batch.id)
        return next
      })
    }
  }

  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '-'
    if (hours < 1) return `${Math.round(hours * 60)}m`
    return `${hours.toFixed(1)}h`
  }

  if (loading) {
    return (
      <PageLayout
        title="Heat Cycling"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-content-secondary">Loading...</div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Heat Cycling"
      user={user}
      onLogout={onLogout}
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={refetch}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Steam Barrel Sensors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SteamBarrelCard barrelId="01" />
          <SteamBarrelCard barrelId="02" />
        </div>

        {/* Cycle History */}
        <CycleHistoryTable />

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Batches Table */}
        {batches.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Equipment</TableHead>
                    <TableHead>Cycle Code</TableHead>
                    <TableHead>Batch Code</TableHead>
                    <TableHead className="text-right">Blocks</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Cooling Time</TableHead>
                    <TableHead className="w-32">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => {
                    const isProcessing = processingIds.has(batch.id)
                    const isHeating = batch.status === 'HEAT_CYCLING'
                    const canMarkReady = batch.can_mark_ready

                    return (
                      <TableRow key={batch.id}>
                        <TableCell>
                          {batch.heat_cycle ? (
                            <div>
                              <span className="font-medium">{batch.heat_cycle.equipment_name}</span>
                              <span className="text-muted-foreground text-sm ml-1">
                                ({batch.heat_cycle.equipment_type})
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {batch.heat_cycle?.cycle_code || '-'}
                        </TableCell>
                        <TableCell className="font-medium">{batch.batch_code}</TableCell>
                        <TableCell className="text-right">{batch.total_blocks}</TableCell>
                        <TableCell>
                          {isHeating ? (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              <Thermometer className="h-3 w-3 mr-1" />
                              Heating
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Snowflake className="h-3 w-3 mr-1" />
                              Cooling
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {batch.hours_cooling !== null ? (
                            <span className={canMarkReady ? 'text-green-600 font-medium' : ''}>
                              {formatHours(batch.hours_cooling)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {!isHeating && (
                            <Button
                              size="sm"
                              variant={canMarkReady ? 'default' : 'outline'}
                              onClick={() => handleMarkReady(batch)}
                              disabled={!canMarkReady || isProcessing || mutationLoading}
                            >
                              {isProcessing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : canMarkReady ? (
                                'Mark Ready'
                              ) : (
                                `${(24 - batch.hours_cooling).toFixed(1)}h left`
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  )
}
